


























































// vue
import { defineComponent, onMounted, ref, watch } from '@vue/composition-api';

// hooks
import { useStore } from '@/store';

// services
import { useService } from '@/vue3/api/services';

// primevue
import ProgressSpinner from 'primevue/progressspinner';

// mapbox
import mapboxgl from 'mapbox-gl';
import geoViewport from '@mapbox/geo-viewport';
import '../../../node_modules/mapbox-gl/dist/mapbox-gl.css';
import MapboxCompare from 'mapbox-gl-compare';

export default defineComponent({
    components: { ProgressSpinner },
    props: {
        isScale: {
            type: Boolean,
            default: false,
        },
        draw: {
            type: Object,
            default: () => {},
        },
        afterCompareLoadCallback: {
            type: Function,
            default: () => {},
        },
        updateCompareMapboxProperty: {
            type: Function,
            default: () => {},
        },
    },
    setup(props: any) {
        // services
        const { getCourseExtent, getDefaultStyle } = useService();

        // hooks
        const store = useStore();

        // refs
        const isLoading = ref(false);
        const isCompareMosaic = ref(false);

        const compareMapbox = ref<mapboxgl.Map | null>(null);
        const leftMap = ref<mapboxgl.Map | null>(null);
        const rightMap = ref<mapboxgl.Map | null>(null);
        const currentHole = ref<number | null>(null);

        const courseExtent = ref<any>({});
        const tileArr = ref<any>([]);

        const leftMosaic = ref('');
        const rightMosaic = ref('');

        // properties
        mapboxgl.accessToken = store.state.mapboxToken;

        // watcher
        watch(
            () => isCompareMosaic.value,
            () => {
                turnOnMosaic(leftMap.value, leftMosaic.value);
                turnOnMosaic(rightMap.value, rightMosaic.value);
            },
        );

        // methods:
        const initMap = (id: string): mapboxgl.Map => {
            let map = new mapboxgl.Map({
                container: id,
                style: 'mapbox://styles/mapbox/satellite-v9',
                center: [0, 0],
                zoom: 2,
                maxZoom: 28,
            });

            return map;
        };

        const refreshCourseExtent = async (): Promise<void> => {
            courseExtent.value = await getCourseExtent(
                store.getters.currentCourseSchema.id,
            );
        };

        const zoomToCourse = (isFly: boolean): void => {
            currentHole.value = null;

            if (isFly) {
                rightMap.value.fitBounds(courseExtent.value.course.bbox);
                return;
            }

            const centroid = [
                (courseExtent.value.course.bbox[2] +
                    courseExtent.value.course.bbox[0]) /
                    2,
                (courseExtent.value.course.bbox[3] +
                    courseExtent.value.course.bbox[1]) /
                    2,
            ];
            const { zoom } = geoViewport.viewport(
                courseExtent.value.course.bbox,
                [
                    compareMapbox.value?.clientWidth,
                    compareMapbox.value?.clientHeight,
                ],
                0,
                24,
                512,
                true,
            );

            rightMap.value.jumpTo({
                center: centroid,
                zoom: zoom,
            });
        };

        const setMosaic = (
            map: mapboxgl.Map,
            selectedMosaicTemp: any,
        ): void => {
            if (map.getLayer('mosaic')) {
                map.removeLayer('mosaic');
            }

            if (map.getSource('clere-mosaic')) {
                try {
                    map.removeSource('clere-mosaic');
                } catch (e) {
                    console.log(e);
                }
            }

            map.addSource(`clere-mosaic`, {
                type: 'raster',
                tileSize: 256,
                tiles: selectedMosaicTemp,
            });

            map.addLayer({
                id: 'mosaic',
                type: 'raster',
                source: 'clere-mosaic',
                minzoom: 0,
                maxzoom: 0,
                layout: {
                    visibility: 'visible',
                },
            });
        };

        const turnOnMap = (map: any, mosaic: string): void => {
            if (!map) return;

            map.on('style.load', async () => {
                map.setFog({
                    color: 'rgb(186, 210, 235)', // Lower atmosphere
                    'high-color': 'rgb(36, 92, 223)', // Upper atmosphere
                    'horizon-blend': 0.01, // Atmosphere thickness (default 0.2 at low zooms)
                    'space-color': 'rgb(11, 11, 25)', // Background color
                    'star-intensity': 0.4, // Background star brightness (default 0.35 at low zoooms )
                });

                map.resize();

                if (props.isScale) {
                    map.addControl(new mapboxgl.ScaleControl());
                }

                zoomToCourse(false);

                const nav = new mapboxgl.NavigationControl({
                    showCompass: true,
                    showZoom: false,
                    visualizePitch: true,
                });

                map.addControl(nav, 'bottom-left');

                map.on('error', (err: any) => {
                    if (
                        err.error.message ===
                        'Could not load image because of The source image could not be decoded.. Please make sure to use a supported image type such as PNG or JPEG. Note that SVGs are not supported.'
                    ) {
                        return;
                    }
                    console.error(err.error);
                });
            });
        };

        const init = async () => {
            isLoading.value = true;
            rightMap.value = initMap('right-map');
            leftMap.value = initMap('left-map');

            const container = '#comparison-container';

            const mapboxCompareInit = new MapboxCompare(    //Don't remove this;
                leftMap.value,
                rightMap.value,
                container
            )

            turnOnMap(leftMap.value, leftMosaic.value);
            turnOnMap(rightMap.value, rightMosaic.value);

            isLoading.value = false;
            
            return mapboxCompareInit;
        };

        const turnOnMosaic = (map: any, mosaic: any): void => {
            tileArr.value = [];
            if (!mosaic) return;

            let tiles = store.state.MaptilerUrl + `/data/${mosaic.key}/{z}/{x}/{y}.png`;

            tileArr.value.push(tiles);

            setMosaic(map, tileArr.value);
        };

        const setHole = (
            hole?: number,
            isGreen?: boolean,
            isJump?: boolean,
        ): void => {
            if (hole === undefined) {
                hole = currentHole.value as number;
            } else if (hole < 1) {
                hole = store.getters.currentCourseSchema.numHoles;
            } else if (hole > store.getters.currentCourseSchema.numHoles) {
                hole = 1;
            }

            if (!hole) {
                return;
            }

            currentHole.value = hole;

            if (!compareMapbox.value) return;

            let extents = isGreen
                ? courseExtent.value.greens
                : courseExtent.value.holes;
            const { zoom } = geoViewport.viewport(
                extents[hole].geom.bbox,
                [
                    compareMapbox.value.clientWidth,
                    compareMapbox.value.clientHeight,
                ],
                0,
                24,
                512,
                true,
            );

            if (isJump) {
                rightMap.value.jumpTo({
                    center: extents[hole].centroid.coordinates,
                    zoom: zoom,
                    bearing:
                        extents[hole].azimuth * (180 / Math.PI) -
                        (isGreen ? 0 : 90),
                    pitch: 0,
                });
                return;
            }

            rightMap.value.flyTo({
                center: extents[hole].centroid.coordinates,
                zoom: zoom,
                bearing:
                    extents[hole].azimuth * (180 / Math.PI) -
                    (isGreen ? 0 : 90),
                pitch: 0,
                duration: isGreen ? 2000 : 1000,
            });
        };

        const toggleCompareMapbox = (value: boolean): void => {
            isCompareMosaic.value = value;
        };

        const clearCompareMosaic = () => {
            isCompareMosaic.value = false;
            leftMosaic.value = '';
            rightMosaic.value = '';

            props.updateCompareMapboxProperty({
                                                isCompareMosaic: false,
                                                leftMosaic: '',
                                                rightMosaic: ''
                                              })
            props.afterCompareLoadCallback(false);
        };

        // lifecycle
        onMounted(async () => {
            await refreshCourseExtent();

            await init();
        });

        return {
            leftMap,
            rightMap,
            compareMapbox,
            init,
            courseExtent,
            setHole,
            currentHole,
            isLoading,
            zoomToCourse,
            isCompareMosaic,
            toggleCompareMapbox,
            leftMosaic,
            rightMosaic,
            clearCompareMosaic,
        };
    },
});
